import { Box, ColorOptions, convertHexToRGBA, LoaderNew, MaterialTextInput, TextNew } from '@jfs/ui-new'
import { padZero } from '@jfs/utils'
import { useIsFocused } from '@react-navigation/native'
import React, { FC, memo, useEffect, useRef, useState } from 'react'
import { InteractionManager } from 'react-native'
import { useStores } from '../../data/store'
import styles from './styles'
import { TextInputProps } from './types'

//COUNTDOWN TIMER VALUE
const COUNTDOWN = 30
const COUNTDOWN_TIMER = 0
const SECOND = 1000
const MAX_INPUT = 6

interface OtpTextInputWithTimerProps extends TextInputProps {
  isLoading: boolean
}

const OtpTextInputWithTimer: FC<OtpTextInputWithTimerProps> = memo(
  ({ isLoading, ...props }: OtpTextInputWithTimerProps) => {
    //Optimise is touched by using ref
    let isTouched = React.useRef(false)
    //IS FOCUSED HOOK
    const isFocused = useIsFocused()
    //TEXT INPUT FOCUS
    const OtpInput = useRef<TextInputProps>(null)

    //SETTING VALUE WHEN TEXT INPUT IS FOCUSED
    const [focused, setFocused] = useState(false)
    const [countDownTimer, setCountDownTimer] = useState(COUNTDOWN)
    const [error, toggleError] = useState('')

    const { theme } = useStores((root) => ({
      theme: root?.config?.merchantTheme,
    }))

    //ERROR CONDITION
    const isError = (props.value as any)?.length < MAX_INPUT && !focused && isTouched?.current
    //CONDITION TO SHOW THE TIMER COMPONENT
    const showTimeTickling = countDownTimer > COUNTDOWN_TIMER && countDownTimer <= COUNTDOWN

    //THIS WILL START THE TIMER.
    useEffect(() => {
      let timer: any
      if (countDownTimer > 0) {
        timer = setInterval(() => setCountDownTimer(countDownTimer - 1), SECOND)
      }
      //Clear all the subscriptions and listeners on unmount
      return () => clearInterval(timer)
    }, [countDownTimer])

    //AUTO FOCUS THE TEXT INPUT
    useEffect(() => {
      // Must run after animations for keyboard to automatically open
      if (isFocused) {
        InteractionManager.runAfterInteractions(() => {
          if (OtpInput?.current) {
            OtpInput?.current?.focus()
          }
        })
      }
    }, [isFocused])

    const onResendOtp = async () => {
      await props?.onResendOtpButtonPress()
      setCountDownTimer(COUNTDOWN)
    }

    //SETTING FOCUSED TO TRUE WHEN USER CLICKS ON TEXT INPUT
    const onFocus = () => {
      isTouched.current = true
      setFocused(true)
      toggleError('')
    }

    //SETTING FOCUSED TO FALSE WHEN USER CLICKS ON TEXT INPUT
    const onBlur = () => {
      setFocused(false)
    }

    //RIGHT COMPONENT OF TEXT INPUT
    const ResendComponent = () => {
      if (isLoading) {
        return <LoaderNew type={'small'} color={theme?.brandColor as ColorOptions} />
      } else {
        return showTimeTickling ? (
          <TextNew
            {...styles.timerText}
            title={`00:${padZero(countDownTimer)}`}
            color={theme?.bodyTextColor as ColorOptions}
          />
        ) : (
          <Box onPress={onResendOtp} testID="resend-otp" underlayColor={'LIGHTEST_GREY'}>
            <TextNew {...styles.resendText} title={'RESEND'} color={theme?.bodyTextColor as ColorOptions} />
          </Box>
        )
      }
    }

    const showError = isError || error

    return (
      <Box {...styles.container} onPress={() => OtpInput?.current?.focus()} underlayColor="TRANSPARENT">
        <Box {...styles.subContainer}>
          <Box {...styles.wrap} underlayColor="TRANSPARENT">
            <Box {...styles.inputBox}>
              <MaterialTextInput
                {...props}
                placeholder={'6 Digit OTP'}
                secureTextEntry
                onFocus={onFocus}
                onBlur={onBlur}
                rightComponent={<ResendComponent />}
                keyboardType="numeric"
                maxLength={6}
                disabled={isLoading}
                isError={showError}
                returnKeyType={props.value?.length > 5 ? 'done' : 'next'}
                placeholderTextColor={convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions}
                color={theme?.bodyTextColor as ColorOptions}
                bottomLineColor={theme?.brandColor as ColorOptions}
                tintColor={theme?.brandColor as ColorOptions}
                testID="otp-input"
              />
            </Box>
          </Box>
        </Box>
        <Box {...styles.errorView}>
          {showError && <TextNew {...styles.errorText}>{error?.length > 1 ? error : 'Invalid Otp'}</TextNew>}
        </Box>
      </Box>
    )
  },
)

export { OtpTextInputWithTimer }
